<template>
  <div>
    <div v-if="!isExpired">
      <passcode-reader
          ref="passcodeReader"
          :icon="icon"
          :title="title"
          :description="description"
          :confirmation-data="confirmationData"
          :is-marked-as-error="isInvalidCodeBackendResponse"
          @errorNotification="sendErrorNotification"
          @validPasscode="setValidPasscode"
          @keyup.enter.native="processEnter" />
    </div>
    <div v-if="isExpired">
      <warning-step
          title="Account Verification expired"
          description="The user a passcode has expired due to multiple failed attempts." />
    </div>
    <div
        v-if="!isProcessing"
        class="account-verification__step__buttons-container">
      <div v-if="verificationMethodType === VERIFICATION_METHODS_MAP.RING_APP_CONTROL_CENTER">
        <el-button
            plain
            type="primary"
            data-test-id="backButton"
            class="account-verification__step__button account-verification__step__button-white"
            @click="notifyBack">
          {{ backButtonText }}
        </el-button>

        <el-button
            type="primary"
            data-test-id="verify_passcode_button"
            class="account-verification__step__button"
            :disabled="!isValidPasscode"
            @click="verifyCode">
          {{ processButtonText }}
        </el-button>
      </div>
      <div v-else>
        <el-button
            v-if="isSingleVerificationChannel && showResendButton"
            plain
            type="primary"
            class="account-verification__step__button account-verification__step__button-white"
            @click="notifyBack">
          {{ resendButtonText }}
        </el-button>
        <el-button
            v-else-if="showResendButton"
            plain
            type="primary"
            data-test-id="try_different_method_button"
            class="account-verification__step__button account-verification__step__button-white"
            @click="tryDifferentMethod">
          {{ tryDifferentMethodButtonText }}
        </el-button>

        <el-button
            type="primary"
            data-test-id="verify_passcode_button"
            class="account-verification__step__button"
            :disabled="!isValidPasscode || isExpired"
            @click="verifyCode">
          {{ processButtonText }}
        </el-button>
      </div>
    </div>

    <processing-info
        v-else-if="isProcessing"
        :message="processingMessage"
        :info-type="processingInfoType"
        class="email-confirmation__processing-info"
        :style="additionalProcessingInfoStyles" />
  </div>
</template>

<script>
import ProcessingInfo from "../shared/ProcessingInfo";
import WarningStep from "../shared/WarningStep";
import PasscodeReader from "./PasscodeReader";
import axios from "axios";

import { createNamespacedHelpers as module } from "vuex";
import { get, isEmpty } from "lodash";

const { mapGetters: accountGetters } = module("db/account");

const { mapActions: sharedActions } = module("shared");

export default {
  name: "PasscodeConfirmationStep",
  components: { PasscodeReader, ProcessingInfo, WarningStep },
  emits: [
    "previousStepRequested",
    "errorNotification",
    "complete",
    "connectionError",
    "tryDifferentMethod",
  ],
  inject: ["VERIFICATION_METHODS_MAP"],
  props: {
    verificationMethodType: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isProcessing: false,
      processingMessage: null,
      processingInfoType: "loading",
      title: null,
      description: null,
      confirmationData: null,
      resendButtonText: null,
      tryDifferentMethodButtonText: null,
      processButtonText: null,
      passcode: null,
      timeoutCounter: null,
      timeoutInterval: null,
      isInvalidCodeBackendResponse: false,
      backButtonText: null,
      isExpired: false,
    };
  },
  computed: {
    ...accountGetters(["customerId", "verificationChannels"]),
    additionalProcessingInfoStyles() {
      switch (this.verificationMethodType) {
        case this.VERIFICATION_METHODS_MAP.EMAIL:
          return "email-confirmation__processing-info";
        default:
          return "";
      }
    },
    isSingleVerificationChannel() {
      return get(this.verificationChannels, "", []).length === 1;
    },
    showResendButton() {
      return !this.timeoutCounter || this.timeoutCounter === 0;
    },
    isValidPasscode() {
      if (!this.passcode || this.isInvalidCodeBackendResponse) {
        return false;
      }

      this.$emit("errorNotification", null);
      return this.passcode.length === 6;
    },
  },
  watch: {
    passcode() {
      this.isInvalidCodeBackendResponse = false;
    },
  },
  mounted() {
    this.resendButtonText = "Resend Code";
    this.processButtonText = "Continue";
    this.processingMessage = "Verifying Code...";
    this.tryDifferentMethodButtonText = "Try Different Method";

    switch (this.verificationMethodType) {
      case this.VERIFICATION_METHODS_MAP.SMS:
        this.title = "We sent the user a passcode.";
        this.description =
          "When they receive it, ask them to repeat it to you.";
        this.confirmationData = "Enter the passcode here:";
        break;
      case this.VERIFICATION_METHODS_MAP.EMAIL:
        this.title = "Ask the user for the passcode we sent.";
        this.description =
          "If they don’t see our email, ask them to check their spam or junk folder.";
        this.confirmationData = "Enter the passcode here:";
        break;
      case this.VERIFICATION_METHODS_MAP.RING_APP_CONTROL_CENTER:
        this.backButtonText = "Back";

        this.title = "The user generated a passcode in the Ring app.";
        this.description =
          "Once they receive it, ask them to repeat it to you.";
        this.confirmationData = "Enter the passcode here:";
        break;
      default:
    }
  },
  methods: {
    ...sharedActions(["fetchPIIAuthorizationStatus"]),
    processEnter() {
      if (isEmpty(this.passcode)) {
        return;
      }

      this.verifyCode();
    },
    sendErrorNotification(message) {
      this.passcode = "";
      this.$emit("errorNotification", message);
    },
    notifyBack() {
      this.$emit("previousStepRequested", true);
    },
    tryDifferentMethod() {
      this.$emit("tryDifferentMethod", true);
    },
    verifyCode() {
      this.isProcessing = true;

      axios
        .post(
          `v3/customers/${this.customerId}/account_verification/verify_code`,
          {
            code: this.passcode,
          },
          { timeout: 20 * 1000 }
        )
        .then((success) => {
          this.processingInfoType = "success";
          this.processingMessage = "Passcode Accepted!";
          this.isInvalidCodeBackendResponse = false;
          setTimeout(() => {
            this.isProcessing = false;
            this.$emit("complete", true);
          }, 1000);
        })
        .catch((error) => {
          this.isProcessing = false;
          this.isInvalidCodeBackendResponse = true;
          this.processError(error);
        });
    },
    processError(error) {
      this.fetchPIIAuthorizationStatus()
        .then((response) => {
          let status = get(response, "data.authorization.status", "expired");
          this.isExpired = status === "expired";
          if (!this.isExpired) {
            let errorMessage = "This code is invalid. Please try again.";
            const errorCode = get(error, "code", get(error, "message", ""));
            if (errorCode === "ECONNABORTED") {
              errorMessage = "Request timed out. Please try again.";
            }
            if (errorCode === "Network Error") {
              this.$emit("connectionError", true);
            } else if (!this.isExpired) {
              this.$emit("errorNotification", errorMessage);
            }
          }
        })
        .catch((error) => {
          console.error(error);
          this.isExpired = true;
        });
    },
    setValidPasscode(passcode) {
      this.passcode = passcode;
    },
  },
};
</script>

<style lang="scss" scoped>
.account-verification__step__buttons-container {
  display: inline-block;
  margin-top: 88px;
  width: 100%;
  text-align: center;
}

/* This is set on additionalProcessingInfoStyles computed property */
.email-confirmation__processing-info {
  margin-top: 78px;
}

/* This is set by additionalCustomIconStyles computed property */
.account-verification__step__custom-email-icon {
  width: 40px;
  height: 28px;
}
</style>
